import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Plan } from 'js/src/client';
import { ApiService, Urls } from 'js/src/client';
import Button, { BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import BillingPeriodToggle from 'js/src/components/pricing/BillingPeriodToggle';
import PlanSwitchErrorHandlerModal from 'js/src/components/pricing/PlanSwitchErrorHandlerModal';
import PricingPlanBlock from 'js/src/components/pricing/PricingPlanBlock';
import SubscriptionChangeModal from 'js/src/components/pricing/SubscriptionChangeModal';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import {
    handlePlanSwitchError as handlePlanSwitchErrorThunk,
    switchToPlan as switchToPlanThunk,
    togglePlanSwitchErrorModal as togglePlanSwitchErrorModalThunk,
    updateSubscription as updateSubscriptionThunk,
} from 'js/src/data/pricing/pricing-operations';
import {
    setAnnualPlans,
    setCouponStatus,
    setMonthlyPlans,
    setSelectedMonthly,
    setShowSubscriptionChangeConfirmation,
    setUserHasPaymentMethodSetup,
} from 'js/src/data/pricing/pricing-slice';
import type { PrettyPlanName } from 'js/src/libs/consts';
import { HUBSPOT_BOOK_CHAT_LINK } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { RootState } from 'js/src/libs/RootState';

const openPricingPageInNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(Urls.PRICING, '_blank');
};

/**
 * Renders the next-level plan inline inside an upsell modal so the user can upgrade
 * without leaving the page. Reuses the pricing page's plan card and in-app upgrade flow.
 *
 * When the target plan can't be resolved (plans failed to load, or the tier isn't
 * available), `onPlanUnavailable` is called so the caller can fall back to the
 * "View pricing options" experience.
 */
const FeatureLockInlinePlan = React.memo(function FeatureLockInlinePlan({
    planDisplayName,
    topHighlight,
    onPlanUnavailable,
}: {
    planDisplayName: PrettyPlanName;
    topHighlight?: string | number;
    onPlanUnavailable: () => void;
}) {
    const dispatch = useDispatch();

    const selectedMonthly = useSelector((state: RootState) => state.pricing.selectedMonthly);
    const monthlyPlans = useSelector((state: RootState) => state.pricing.monthlyPlans);
    const annualPlans = useSelector((state: RootState) => state.pricing.annualPlans);
    const couponStatus = useSelector((state: RootState) => state.pricing.couponStatus);
    const showSubscriptionChangeConfirmation = useSelector(
        (state: RootState) => state.pricing.showSubscriptionChangeConfirmation,
    );
    const confirmationModalData = useSelector(
        (state: RootState) => state.pricing.confirmationModalData,
    );
    const showPlanSwitchErrorModal = useSelector(
        (state: RootState) => state.pricing.showPlanSwitchErrorModal,
    );
    const planSwitchErrorInfo = useSelector(
        (state: RootState) => state.pricing.planSwitchErrorInfo,
    );

    const [loadFailed, setLoadFailed] = React.useState(false);

    const plans = selectedMonthly ? monthlyPlans : annualPlans;
    const plansLoaded = monthlyPlans.length > 0 && annualPlans.length > 0;

    React.useEffect(() => {
        if (plansLoaded) {
            return;
        }
        ApiService.pricingPlansRetrieve({ query: { coupon: '' } })
            .then((response) => {
                dispatch(setMonthlyPlans(response.data.monthly_plans));
                dispatch(setAnnualPlans(response.data.annual_plans));
                dispatch(setCouponStatus(response.data.coupon_status));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                setLoadFailed(true);
            });
    }, [dispatch, plansLoaded]);

    React.useEffect(() => {
        if (SERVER.USER === null) {
            return;
        }
        ApiService.pricingPaymentMethodStatusRetrieve()
            .then((response) => {
                dispatch(setUserHasPaymentMethodSetup(response.data.has_payment_configured));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
            });
    }, [dispatch]);

    const targetPlan = React.useMemo(
        () => plans.find((plan) => plan.display_name === planDisplayName),
        [plans, planDisplayName],
    );

    // Once plans have loaded, if the requested tier isn't there, let the caller fall back.
    React.useEffect(() => {
        if (loadFailed || (plansLoaded && !targetPlan)) {
            onPlanUnavailable();
        }
    }, [loadFailed, plansLoaded, targetPlan, onPlanUnavailable]);

    // This modal is an upgrade prompt, so the plan we surface should always cost more than
    // the user's current plan. If it's a downgrade, the feature-to-plan mapping is wrong —
    // report it so we can catch the misconfiguration.
    React.useEffect(() => {
        if (
            !targetPlan ||
            !plansLoaded ||
            SERVER.USER === null ||
            SERVER.USER.plan === '' ||
            SERVER.USER.is_manual_billing
        ) {
            return;
        }

        // Compare prices within the user's own billing cycle to avoid false positives: a
        // higher tier's annual-discounted monthly-equivalent can dip below a lower tier's
        // full monthly rate, which would look like a downgrade even though it isn't. Look up
        // the target tier's price in the same cycle the user is billed on.
        const userBilledAnnually = SERVER.USER.plan_billing_cycle === 'annual';
        const plansInUserCycle = userBilledAnnually ? annualPlans : monthlyPlans;
        const targetPlanInUserCycle = plansInUserCycle.find(
            (plan) => plan.display_name === planDisplayName,
        );
        if (!targetPlanInUserCycle) {
            return;
        }

        if (targetPlanInUserCycle.price < SERVER.USER.plan_price) {
            ErrorReporter.capture(
                new Error(
                    `FeatureLockInlinePlan surfaced a downgrade: plan "${targetPlanInUserCycle.name}" ` +
                        `($${targetPlanInUserCycle.price}) is cheaper than the user's current plan ` +
                        `"${SERVER.USER.plan}" ($${SERVER.USER.plan_price}) in the ` +
                        `${SERVER.USER.plan_billing_cycle} billing cycle`,
                ),
            );
        }
    }, [targetPlan, plansLoaded, annualPlans, monthlyPlans, planDisplayName]);

    const switchToPlan = React.useCallback(
        (plan: Plan) => {
            // Open Stripe checkout in a new tab so the user's current work isn't lost to a
            // full-page redirect when they upgrade from inside the app.
            dispatch(switchToPlanThunk(plan, true));
        },
        [dispatch],
    );

    const handlePlanSwitchError = React.useCallback(
        (errorInfo: PlanSwitchErrorInfo) => {
            dispatch(handlePlanSwitchErrorThunk(errorInfo));
        },
        [dispatch],
    );

    const updateSubscription = React.useCallback(
        (email: string, plan: Plan) => {
            dispatch(updateSubscriptionThunk(email, plan));
        },
        [dispatch],
    );

    const closeSubscriptionChangeModal = React.useCallback(() => {
        dispatch(setShowSubscriptionChangeConfirmation(false));
    }, [dispatch]);

    const togglePlanSwitchErrorModal = React.useCallback(
        (visibilityFlag: boolean) => {
            dispatch(togglePlanSwitchErrorModalThunk(visibilityFlag));
        },
        [dispatch],
    );

    const selectMonthly = React.useCallback(() => {
        dispatch(setSelectedMonthly(true));
    }, [dispatch]);

    const selectYearly = React.useCallback(() => {
        dispatch(setSelectedMonthly(false));
    }, [dispatch]);

    // Manual-billing users (e.g. enterprise) have no self-serve Stripe subscription, so the
    // in-app upgrade flow can't work for them. Point them at their account representative.
    if (SERVER.USER?.is_manual_billing) {
        return (
            <div className="d-flex flex-column align-items-center text-center">
                <p className="font-l5 font-l5--small color-grey-800 mb-l5-16">
                    Your plan is managed by your account representative. Reach out to them to unlock
                    this feature.
                </p>
                <Button
                    text="Contact your account representative"
                    variant={BUTTON_VARIANTS.FILLED}
                    href={HUBSPOT_BOOK_CHAT_LINK}
                    hrefTarget="_blank"
                />
            </div>
        );
    }

    if (loadFailed) {
        return null;
    }

    return (
        <>
            <div className="d-flex justify-content-center mb-l5-16">
                <BillingPeriodToggle
                    monthly={selectedMonthly}
                    selectMonthly={selectMonthly}
                    selectYearly={selectYearly}
                />
            </div>
            {/* `plan === undefined` renders the skeleton loading state */}
            <div className="w-100 d-flex justify-content-center">
                <PricingPlanBlock
                    plan={plansLoaded ? targetPlan : undefined}
                    switchToPlan={switchToPlan}
                    handlePlanSwitchError={handlePlanSwitchError}
                    andMoreClicked={openPricingPageInNewTab}
                    upgradeButtonVariant={BUTTON_VARIANTS.FILLED}
                    topHighlight={topHighlight}
                    compact
                />
            </div>
            <SubscriptionChangeModal
                showing={showSubscriptionChangeConfirmation}
                subscription_data={confirmationModalData}
                coupon_data={couponStatus}
                update_subscription={updateSubscription}
                close_subscription_change_modal={closeSubscriptionChangeModal}
            />
            <PlanSwitchErrorHandlerModal
                showing={showPlanSwitchErrorModal}
                errorInfo={planSwitchErrorInfo}
                onModalClose={togglePlanSwitchErrorModal}
            />
        </>
    );
});

export default FeatureLockInlinePlan;
